import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ChevronLeft, ChevronRight, MapPin, Clock, Utensils, Coffee, LogIn, LogOut, AlertTriangle, ExternalLink } from "lucide-react";
import AttendanceMapDialog from "./AttendanceMapDialog";

const KIND_LABEL = { in: "Entrata", out: "Uscita", lunch_start: "Pausa pranzo", lunch_end: "Fine pausa" };
const KIND_COLOR = { in: "bg-emerald-100 text-emerald-800 border-emerald-300", out: "bg-blue-100 text-blue-800 border-blue-300", lunch_start: "bg-amber-100 text-amber-800 border-amber-300", lunch_end: "bg-amber-50 text-amber-700 border-amber-200" };
const KIND_ICON = { in: LogIn, out: LogOut, lunch_start: Utensils, lunch_end: Coffee };

function fmtTime(ts) { try { return new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } }
function monthLabel(m) { try { const [y, mo] = m.split("-"); return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("it-IT", { month: "long", year: "numeric" }); } catch { return m; } }
function addMonth(m, delta) { const [y, mo] = m.split("-").map(Number); const d = new Date(y, mo - 1 + delta, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function ymd(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }

export default function AttendanceCalendar({ embedded = true }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // attendance for map

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/attendances/my-month?month=${month}`);
      setItems(r.data || []);
    } catch { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line

  // Group by date
  const byDay = useMemo(() => {
    const m = {};
    for (const a of items) {
      const key = a.timestamp.slice(0, 10);
      if (!m[key]) m[key] = [];
      m[key].push(a);
    }
    return m;
  }, [items]);

  // Build 6-week grid
  const grid = useMemo(() => {
    const [y, mo] = month.split("-").map(Number);
    const firstOfMonth = new Date(y, mo - 1, 1);
    const startDay = (firstOfMonth.getDay() + 6) % 7; // Mon=0
    const start = new Date(y, mo - 1, 1 - startDay);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [month]);

  const today = ymd(new Date());

  // Stats
  const totalDays = Object.keys(byDay).length;
  const validCount = items.filter(a => a.valid).length;
  const invalidCount = items.length - validCount;

  return (
    <div className={`${embedded ? "" : "p-4 md:p-12"}`} data-testid="attendance-calendar">
      {!embedded && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Le mie timbrature</div>
          <h1 className="font-display text-4xl">Calendario personale</h1>
        </div>
      )}

      {/* Header con nav mese + stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button onClick={() => setMonth(addMonth(month, -1))} variant="outline" size="sm" className="rounded-full" data-testid="cal-prev-month"><ChevronLeft className="w-4 h-4"/></Button>
          <div className="font-display text-xl capitalize min-w-[160px] text-center" data-testid="cal-month-label">{monthLabel(month)}</div>
          <Button onClick={() => setMonth(addMonth(month, 1))} variant="outline" size="sm" className="rounded-full" data-testid="cal-next-month"><ChevronRight className="w-4 h-4"/></Button>
          <Button onClick={() => setMonth(new Date().toISOString().slice(0,7))} variant="ghost" size="sm" className="rounded-full ml-1 text-xs">Oggi</Button>
        </div>
        <div className="flex gap-2 text-xs">
          <span className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="cal-stat-days">{totalDays} giorni</span>
          <span className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">{validCount} valide</span>
          {invalidCount > 0 && <span className="px-2 py-1 rounded-lg bg-red-50 text-red-700 border border-red-200">{invalidCount} fuori area</span>}
        </div>
      </div>

      {/* Calendar grid */}
      <div className="card-smart overflow-hidden">
        <div className="grid grid-cols-7 bg-[#0A0A0A] text-white">
          {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map(d => (
            <div key={d} className="text-center py-2 text-[10px] uppercase tracking-widest font-bold">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 bg-white">
          {grid.map((d, i) => {
            const key = ymd(d);
            const inMonth = d.getMonth() === Number(month.split("-")[1]) - 1;
            const isToday = key === today;
            const ats = byDay[key] || [];
            const hasAnomalies = ats.some(a => !a.valid);
            return (
              <div
                key={i}
                className={`border-b border-r border-[#1F2540] min-h-[88px] md:min-h-[110px] p-1.5 flex flex-col ${inMonth ? "" : "bg-[#FAFAFA] opacity-50"} ${isToday ? "ring-2 ring-[#3B82F6] ring-inset" : ""}`}
                data-testid={`cal-day-${key}`}
              >
                <div className={`text-xs font-semibold mb-1 flex items-center justify-between ${isToday ? "text-[#3B82F6]" : "text-[#0A0A0A]"}`}>
                  <span>{d.getDate()}</span>
                  {hasAnomalies && <AlertTriangle className="w-3 h-3 text-red-500"/>}
                </div>
                <div className="flex flex-col gap-1 flex-1 overflow-hidden">
                  {ats.slice(0, 4).map((a) => {
                    const Icon = KIND_ICON[a.kind] || Clock;
                    const wmTip = a.work_mode && a.work_mode !== "office"
                      ? ` · ${a.work_mode === "smartworking" ? "Smart" : "Trasferta"}`
                      : "";
                    return (
                      <button
                        key={a.id}
                        onClick={() => setSelected(a)}
                        data-testid={`cal-att-${a.id}`}
                        className={`text-[10px] px-1.5 py-0.5 rounded border ${KIND_COLOR[a.kind] || "bg-neutral-100"} ${!a.valid ? "ring-1 ring-red-300" : ""} flex items-center gap-1 hover:opacity-80 transition`}
                        title={`${KIND_LABEL[a.kind] || a.kind} · ${fmtTime(a.timestamp)} · ${a.qr_label || ""}${wmTip}${a.note ? " · " + a.note : ""}`}
                      >
                        <Icon className="w-2.5 h-2.5 flex-shrink-0"/>
                        <span className="truncate">{fmtTime(a.timestamp)}</span>
                        {a.work_mode === "smartworking" && <span className="text-emerald-700 font-bold">⌂</span>}
                        {a.work_mode === "trasferta" && <span className="text-[#7C3AED] font-bold">✈</span>}
                      </button>
                    );
                  })}
                  {ats.length > 4 && (
                    <div className="text-[9px] text-[#999] px-1">+{ats.length - 4} altre</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-center py-4 text-sm text-[#757575]">Caricamento…</div>}

      {selected && (
        <AttendanceMapDialog att={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
